<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDetectKeyboardLayout } from 'dashboard/composables/useDetectKeyboardLayout';
import { SHORTCUT_KEYS, KEYS } from './constants';
import {
  LAYOUT_QWERTZ,
  keysToModifyInQWERTZ,
} from 'shared/helpers/KeyboardHelpers';
import Hotkey from 'dashboard/components/base/Hotkey.vue';

defineProps({ show: Boolean });
const emit = defineEmits(['close', 'update:show']);

const { t } = useI18n();
const currentLayout = ref(null);

const title = computed(
  () => item => t(`KEYBOARD_SHORTCUTS.TITLE.${item.label}`)
);

// Added this function to check if the keySet needs a shift key
// This is used to display the shift key in the modal
// If the current layout is QWERTZ and the keySet contains a key that needs a shift key
// If layout is QWERTZ then we add the Shift+keysToModify to fix an known issue
// https://github.com/chatwoot/chatwoot/issues/9492
const needsShiftKey = computed(
  () => keySet =>
    currentLayout.value === LAYOUT_QWERTZ &&
    keySet.some(key => keysToModifyInQWERTZ.has(key))
);

const handleClose = () => {
  emit('update:show', false);
  emit('close');
};

onMounted(async () => {
  currentLayout.value = await useDetectKeyboardLayout();
});
</script>

<template>
  <woot-modal :show="show" size="medium" @close="handleClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS')"
      />
      <div class="grid grid-cols-2 px-10 pt-0 pb-4 mt-8 gap-x-12 gap-y-4">
        <div class="flex justify-between items-center min-w-0">
          <h5 class="text-sm font-medium text-muted-foreground">
            {{ $t('KEYBOARD_SHORTCUTS.TOGGLE_MODAL') }}
          </h5>
          <div class="flex items-center gap-2 mb-1">
            <Hotkey
              custom-class="min-h-[28px] min-w-[60px] normal-case key-modern shadow-sm"
            >
              {{ KEYS.WIN }}
            </Hotkey>
            <Hotkey
              custom-class="min-h-[28px] min-w-[36px] key-modern shadow-sm"
            >
              {{ KEYS.SLASH }}
            </Hotkey>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 px-10 pt-0 pb-10 gap-x-12 gap-y-4">
        <div
          v-for="shortcut in SHORTCUT_KEYS"
          :key="shortcut.id"
          class="flex justify-between items-center min-w-0"
        >
          <h5 class="text-sm font-medium text-muted-foreground min-w-[36px]">
            {{ title(shortcut) }}
          </h5>
          <div class="flex items-center gap-2 mb-1">
            <template v-if="needsShiftKey(shortcut.keySet)">
              <Hotkey
                custom-class="min-h-[28px] min-w-[36px] key-modern shadow-sm"
              >
                {{ KEYS.SHIFT }}
              </Hotkey>
            </template>

            <template v-for="(key, index) in shortcut.displayKeys" :key="index">
              <template v-if="key !== KEYS.SLASH">
                <Hotkey
                  custom-class="min-h-[28px] min-w-[36px] key-modern normal-case shadow-sm"
                >
                  {{ key }}
                </Hotkey>
              </template>
              <span
                v-else
                class="flex items-center text-sm font-bold text-foreground mx-1"
              >
                {{ key }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.key-modern {
  @apply py-1.5 px-3 font-bold text-[11px] text-foreground bg-muted border border-border rounded-lg inline-flex items-center justify-center;
}
</style>
